import { apiGet, apiPut } from './client';

/**
 * MKS-361 — time-off substitutes: who covers whom, on which projects.
 *
 * A module of its own rather than more functions in `timeOff.ts`: these four
 * endpoints answer a different question from "what time off does this person
 * have", and three different surfaces (the request form, the approval queue,
 * the project page) use them without touching the time-off list at all.
 */

/** Where a possible stand-in comes from. */
export type SubstituteSource = 'project' | 'role_tag';

export interface SubstituteCandidate {
  userId: number;
  name: string;
  email: string;
  source: SubstituteSource;
  /**
   * They are themselves away on at least one day of the request. Candidates
   * who are away are not offered at all, so this is only ever true for a
   * substitute already stored who has since filed their own time off.
   */
  awayDuringRequest: boolean;
}

export interface CoverageProjectOption {
  projectId: number;
  projectName: string;
  roleId: number | null;
  roleName: string | null;
  candidates: SubstituteCandidate[];
  selected: { substituteUserId: number; substituteName: string; note: string | null } | null;
  /**
   * May the signed-in user change this project's substitute? The server's
   * answer to its own write rule — admin, or this project's PM — so the
   * read-only rendering never has to guess.
   */
  canEdit: boolean;
}

/** A role on a project with nobody left on the given days. */
export interface CoverageWarning {
  projectId: number;
  projectName: string;
  roleId: number;
  roleName: string | null;
  dates: string[];
  peersAway: { userId: number; name: string }[];
}

export interface CoverageOptions {
  projects: CoverageProjectOption[];
  warnings: CoverageWarning[];
}

export interface SubstituteSelection {
  projectId: number;
  substituteUserId: number;
  note?: string | null;
}

export interface StoredSubstitute {
  projectId: number;
  projectName: string;
  substituteUserId: number;
  substituteName: string;
  note: string | null;
}

export interface TimeOffCoverage {
  timeOffId: number;
  /** Every project the request affects, covered or not. */
  projects: { projectId: number; projectName: string }[];
  substitutes: StoredSubstitute[];
  warnings: CoverageWarning[];
}

export interface ProjectCoverageEntry {
  timeOffId: number;
  userId: number;
  userName: string;
  typeName: string | null;
  typeColour: string;
  dateFrom: string;
  dateTo: string;
  state: string | null;
  substitute: { userId: number; name: string; note: string | null } | null;
}

/**
 * The form's payload. Pass `timeOffId` for a request that exists, or
 * `dateFrom`/`dateTo` (plus the type, so a presence request can answer
 * "nothing to pick") for one that does not yet.
 */
export async function getCoverageOptions(params: {
  dateFrom?: string;
  dateTo?: string;
  timeOffTypeId?: number;
  timeOffId?: number;
}): Promise<CoverageOptions> {
  const query = new URLSearchParams();
  if (params.timeOffId !== undefined) query.set('timeOffId', String(params.timeOffId));
  if (params.dateFrom) query.set('dateFrom', params.dateFrom);
  if (params.dateTo) query.set('dateTo', params.dateTo);
  if (params.timeOffTypeId !== undefined) query.set('timeOffTypeId', String(params.timeOffTypeId));
  const res = await apiGet<{ data: CoverageOptions }>(`/time-off/coverage/options?${query}`);
  return res.data;
}

/** Substitutes and overlap warnings for the whole approval queue, in one go. */
export async function getPendingCoverage(): Promise<TimeOffCoverage[]> {
  const res = await apiGet<{ data: TimeOffCoverage[] }>('/time-off/coverage/pending');
  return res.data;
}

/** Replace the whole substitute set on one request. */
export async function saveSubstitutes(
  timeOffId: number,
  substitutes: SubstituteSelection[],
): Promise<StoredSubstitute[]> {
  const res = await apiPut<{ data: StoredSubstitute[] }>(`/time-off/${timeOffId}/substitutes`, {
    substitutes,
  });
  return res.data;
}

/** Who is out of a project between two dates, and who covers them. */
export async function getProjectTimeOffCoverage(
  projectId: number,
  from: string,
  to: string,
): Promise<ProjectCoverageEntry[]> {
  const res = await apiGet<{ data: ProjectCoverageEntry[] }>(
    `/projects/${projectId}/time-off-coverage?from=${from}&to=${to}`,
  );
  return res.data;
}
